import React, { useMemo, useState } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { ArrowLeft, Search, ArrowUpDown, Filter, BarChart3 } from 'lucide-react';
import {
  nationalOverview,
  getRegionById,
  getSchoolsForRegion,
  getClassesForSchool,
  getSchoolById,
} from '../../../data/layeredMockData';
import { LevelMetrics } from '../../../types';
import Pagination from '../../../../../shared/components/Pagination';

const BASE = '/kvs-samagam/student-analytics/stages/preparatory';

const LEVEL_SLUGS: Record<string, string> = {
  excellent: 'Excellent',
  good: 'Good',
  average: 'Average',
  'below-average': 'Below Average',
};

function getCompetencyLevel(avg: number): string {
  if (avg >= 85) return 'Excellent';
  if (avg >= 70) return 'Good';
  if (avg >= 55) return 'Average';
  return 'Below Average';
}

function levelColor(level: string): string {
  switch (level) {
    case 'Excellent': return 'bg-emerald-50 text-emerald-700';
    case 'Good': return 'bg-indigo-50 text-indigo-700';
    case 'Average': return 'bg-amber-50 text-amber-700';
    default: return 'bg-red-50 text-red-700';
  }
}

interface EntityRow {
  id: string;
  name: string;
  subtitle: string;
  metrics: LevelMetrics;
  level: string;
  navigateTo: string;
}

type SortKey = 'name' | 'students' | 'competency' | 'needsSupport' | 'excellent';

interface EntityListScreenProps {
  entityType: 'regions' | 'schools' | 'classes';
}

const EntityListScreen: React.FC<EntityListScreenProps> = ({ entityType }) => {
  const { regionId, schoolId, level } = useParams<{ regionId?: string; schoolId?: string; level?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const filterLevel = level ? LEVEL_SLUGS[level] : undefined;
  const academicYear = Number(searchParams.get('year') || '2025');
  const termValue = searchParams.get('term') || 'overall';
  const querySuffix = `?year=${academicYear}&term=${termValue}`;

  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('competency');
  const [sortAsc, setSortAsc] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 15;

  const allEntities: EntityRow[] = useMemo(() => {
    if (entityType === 'regions') {
      return nationalOverview.regions.map(r => ({
        id: String(r.regionId),
        name: r.regionName,
        subtitle: `State: ${r.stateCode}`,
        metrics: r.metrics,
        level: getCompetencyLevel(r.metrics.avgCompetency),
        navigateTo: `${BASE}/region/${r.regionId}${querySuffix}`,
      }));
    }
    if (entityType === 'schools') {
      const schools = getSchoolsForRegion(Number(regionId));
      return schools.map(s => ({
        id: s.schoolId,
        name: s.schoolName,
        subtitle: s.location,
        metrics: s.metrics,
        level: getCompetencyLevel(s.metrics.avgCompetency),
        navigateTo: `${BASE}/region/${regionId}/school/${s.schoolId}${querySuffix}`,
      }));
    }
    const classes = getClassesForSchool(schoolId!);
    return classes.map(c => ({
      id: c.classId,
      name: `${c.className} ${c.section}`,
      subtitle: `Teacher: ${c.teacherName}`,
      metrics: c.metrics,
      level: getCompetencyLevel(c.metrics.avgCompetency),
      navigateTo: `${BASE}/region/${regionId}/school/${schoolId}/class/${c.classId}${querySuffix}`,
    }));
  }, [entityType, querySuffix, regionId, schoolId]);

  const filteredEntities = useMemo(() => {
    let list = [...allEntities];
    if (filterLevel) {
      list = list.filter(e => e.level === filterLevel);
    }
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(e => e.name.toLowerCase().includes(q) || e.subtitle.toLowerCase().includes(q));
    }
    list.sort((a, b) => {
      switch (sortKey) {
        case 'name': return sortAsc ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name);
        case 'students': return sortAsc ? a.metrics.totalStudents - b.metrics.totalStudents : b.metrics.totalStudents - a.metrics.totalStudents;
        case 'competency': return sortAsc ? a.metrics.avgCompetency - b.metrics.avgCompetency : b.metrics.avgCompetency - a.metrics.avgCompetency;
        case 'needsSupport': return sortAsc ? a.metrics.needsSupportCount - b.metrics.needsSupportCount : b.metrics.needsSupportCount - a.metrics.needsSupportCount;
        case 'excellent': return sortAsc ? a.metrics.excellentCount - b.metrics.excellentCount : b.metrics.excellentCount - a.metrics.excellentCount;
        default: return 0;
      }
    });
    return list;
  }, [allEntities, filterLevel, search, sortKey, sortAsc]);

  const totalPages = Math.ceil(filteredEntities.length / pageSize);
  const pageEntities = filteredEntities.slice((page - 1) * pageSize, page * pageSize);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortAsc(!sortAsc);
    else { setSortKey(key); setSortAsc(false); }
  };

  const entityLabel = entityType === 'regions' ? 'Regions' : entityType === 'schools' ? 'Schools' : 'Classes';
  let parentLabel = '';
  if (entityType === 'schools') {
    const regionSummary = getRegionById(Number(regionId));
    parentLabel = regionSummary ? ` — ${regionSummary.regionName} Region` : '';
  } else if (entityType === 'classes') {
    const school = getSchoolById(Number(regionId), schoolId!);
    parentLabel = school ? ` — ${school.schoolName}` : '';
  }

  const title = filterLevel ? `${filterLevel} ${entityLabel}` : `All ${entityLabel}`;

  const buildFilterPath = (lvl: string, active: boolean) => {
    const slug = lvl.toLowerCase().replace(/\s+/g, '-');
    if (entityType === 'regions') return `${BASE}/list/regions${active ? '' : `/${slug}`}${querySuffix}`;
    if (entityType === 'schools') return `${BASE}/region/${regionId}/list/schools${active ? '' : `/${slug}`}${querySuffix}`;
    return `${BASE}/region/${regionId}/school/${schoolId}/list/classes${active ? '' : `/${slug}`}${querySuffix}`;
  };

  const SortHeader: React.FC<{ label: string; k: SortKey }> = ({ label, k }) => (
    <th
      className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase cursor-pointer hover:text-indigo-600 select-none"
      onClick={() => toggleSort(k)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <ArrowUpDown size={12} className={sortKey === k ? 'text-indigo-600' : 'text-gray-300'} />
      </span>
    </th>
  );

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-6">
      {/* Back + Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500">
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
          <p className="text-sm text-gray-500">
            {filteredEntities.length} {entityLabel.toLowerCase()}{parentLabel}
            {filterLevel && (
              <span className={`ml-2 text-xs font-semibold px-2 py-0.5 rounded-full ${levelColor(filterLevel)}`}>
                Filtered: {filterLevel}
              </span>
            )}
          </p>
        </div>
        {entityType === 'regions' && (
          <button
            onClick={() => navigate(`${BASE}/school-performance${querySuffix}`)}
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white text-sm font-semibold px-4 py-2 transition-colors shadow-sm"
          >
            <BarChart3 size={16} />
            School Performance
          </button>
        )}
      </div>

      {/* Filter chips + search */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Filter size={14} className="text-gray-400" />
          {['Excellent', 'Good', 'Average', 'Below Average'].map(lvl => {
            const isActive = filterLevel === lvl;
            return (
              <button
                key={lvl}
                onClick={() => navigate(buildFilterPath(lvl, isActive))}
                className={`text-xs font-medium px-3 py-1.5 rounded-full border transition ${
                  isActive
                    ? `${levelColor(lvl)} border-current`
                    : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
                }`}
              >
                {lvl} ({allEntities.filter(e => e.level === lvl).length})
              </button>
            );
          })}
        </div>
        <div className="relative">
          <div className="flex items-center gap-2">
            <div className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
              <label htmlFor="term" className="text-xs font-medium text-gray-600">Term</label>
              <select
                id="term"
                value={termValue}
                onChange={(e) => navigate(`${location.pathname}?year=${academicYear}&term=${e.target.value}`)}
                className="text-sm text-gray-700 bg-transparent focus:outline-none"
              >
                <option value="overall">Overall</option>
                <option value="1">Term 1</option>
                <option value="2">Term 2</option>
              </select>
            </div>
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
                placeholder={`Search ${entityLabel.toLowerCase()}...`}
                className="pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-200 w-56"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <SortHeader label="Name" k="name" />
                <SortHeader label="Students" k="students" />
                <SortHeader label="Competency" k="competency" />
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Level</th>
                <SortHeader label="Need Support" k="needsSupport" />
                <SortHeader label="Excellent" k="excellent" />
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Behavior</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">BMI Normal</th>
              </tr>
            </thead>
            <tbody>
              {pageEntities.map(e => (
                <tr
                  key={e.id}
                  onClick={() => navigate(e.navigateTo)}
                  className="border-b border-gray-100 hover:bg-indigo-50/40 cursor-pointer transition"
                >
                  <td className="px-4 py-3">
                    <p className="text-sm font-medium text-gray-900">{e.name}</p>
                    <p className="text-[11px] text-gray-400">{e.subtitle}</p>
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900">{e.metrics.totalStudents.toLocaleString()}</td>
                  <td className="px-4 py-3">
                    <span className={`text-sm font-semibold ${e.metrics.avgCompetency >= 85 ? 'text-emerald-600' : e.metrics.avgCompetency >= 70 ? 'text-amber-600' : 'text-red-600'}`}>
                      {e.metrics.avgCompetency}%
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${levelColor(e.level)}`}>{e.level}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-sm font-semibold ${e.metrics.needsSupportCount === 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {e.metrics.needsSupportCount.toLocaleString()}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm font-semibold text-emerald-600">{e.metrics.excellentCount.toLocaleString()}</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">{e.metrics.avgBehaviorScore}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{e.metrics.bmiNormalPercent}%</td>
                </tr>
              ))}
              {pageEntities.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-sm text-gray-400">
                    No {entityLabel.toLowerCase()} found{filterLevel ? ` with level "${filterLevel}"` : ''}.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="px-5 py-3 border-t border-gray-100">
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              totalRecords={filteredEntities.length}
              pageSize={pageSize}
              onPageChange={setPage}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default EntityListScreen;
